import { all, EntryEvent, ReplacedEvent, reactive, Variable, VArray, VNumber, P, Div } from 'alkali'
import { analysisType, structuredQuery, analyzeTreatmentAs, getBluntQuery, isLiveEditing, analysisSelections, sorting, includeFlagged, encodeAttributeAsValue, isAnalysisOutcomePositive } from './SearchQuery'
import { studiesSearch, searchForStudies, selectedOutcomes, selectedTreatmentIds } from './EvidenceSearch'
import { StudyAnalysisModel, getPublicationId } from './AnalyzeStudy'
import { Cache } from 'tools/Index'
import { get, post, patch } from 'tools/request'
import { bindToHashParameter } from '../app/navigation'
import { app } from '../app/app'
import { track } from 'components/tracking/track'
import { getToastPosition, Toast } from 'components/generic/Toast'
import { studyDetailsPane } from 'components/studies/StudyDetailsPane'

export let controlTreatment = null
const FETCH_SIZE = 50

export const Study = Variable.with({
	selectedOutcome: {}
})

export const flagFiltered = reactive(0)

export const studyStats = reactive({
	analyzableCount: 0,
	undigitizedCount: 0,
	groupedCount: 0,
	digitizedCount: 0,
})

export const tabFilter = reactive('')
export const activeTab = reactive('sources')
bindToHashParameter({
	variable: activeTab,
	parameter: 'view',
	defaultValue: 'sources',
})

let firstTab = true
activeTab.subscribe(event => {
	if (firstTab)
		firstTab = false
	else
		track({ event: 'tab-' + activeTab})
})

export const focusedStudyId = reactive(null)
bindToHashParameter({
	variable: focusedStudyId,
	parameter: 'study'
})
export async function getFocusedStudy() {
	let study = focusedStudy.valueOf()
	if (study)
		return study
	await whenLastLoading
	study = (await studyCollection)?.[0]
	if (!study)
		await whenLastLoading
	study = (await studyCollection)?.[0]
	if (study)
		focusedStudy.put(study)
	return study
}

export const lastDocSearchQuery = reactive('')
export const articleCountCache = new Cache()
export const getArticleCountForIntervention = (intervention, outcome) => new VNumber(structuredQuery.then(async ({ characteristics, study }) => {
	let bluntQuery = await getBluntQuery({ interventions: [intervention], outcomes: [], characteristics, forArticles: true })
	return articleCountCache.get(bluntQuery, () => get('/portal/article-count?query=' + encodeURIComponent(bluntQuery)))
}))

let digitizedOffset = 0, digitizedQueue = [], loadingDigitized = true // the queue of loaded studies, that haven't been merged into the final results yet
export function hasMoreDigitized() {
	return loadingDigitized || digitizedQueue.length > 0
}
export let digitizedStudiesLoaded = reactive([])
let requestId = 1,
	undigitizedOffset = 0,
	loadingUndigitizedLevel = 1, // 3 is the narrowest search
	hasLoadedUndigitizedCount,
	undigitizedQueue = [] // the queue of loaded undigitized studies, that haven't been merged into the final results yet
export let allArticles = []
export let allArticlesVar = reactive([])
export const studyCollection = all([studiesSearch, selectedOutcomes, analysisType], ({ studies, treatments, count, analyzableTrialCount }, selectedOutcomes, analysisType) => {
	studyStats.digitizedCount.put(count)
	studyStats.analyzableCount.put(analyzableTrialCount)
	allArticlesVar.put(allArticles = [])
	return studies
}).as(VArray)
Study.collection = studyCollection

const MissingResultsToast = Toast('#doc-search-results-toast.active.top-arrow', {
		id: 'doc-search-results-toast',
		title: 'Missing Studies',
		message: Div([
			P('There may be missing studies in the search results due to DocSearch being down.'),
			P('Please check again shortly or contact your Client Success representative.')
		]),
		hoverClose: false
})

export function findStudyById([id, collection], depth?) {
	depth = depth || 0
	if (depth > 10)
		return
	let study = allArticles?.find(study => study.id == id)
	if (!study)
		return Promise.resolve(whenLastLoading).then(() => findStudyById([id, collection], depth + 1))
	let publicationId = getPublicationId(id).valueOf() || id
	return study.additionalStudies?.find((study) => study.id == publicationId) || study
}
export const focusedStudy = all([focusedStudyId, studyCollection]).to(findStudyById,
	study => focusedStudyId.put(study?.parentStudy?.id || study?.id))
window.focusedStudy = focusedStudy
window.studyCollection = studyCollection

export let whenLastLoading
let studiesByIds
studyCollection.slice = (start, end) => {
	let results = whenLastLoading = getSliceResults(start, end)
	results.totalLength = results.then(results => {
		// estimate total length
		return start + results.length + digitizedQueue.length + (loadingDigitized ? 20 : 0) + undigitizedQueue.length + (loadingUndigitizedLevel ? 20 : 0)
	})
	return results
}

export let whenDigitizedLoaded, whenUndigitizedLoaded, digitizedResolved
async function getSliceResults(start, end) {
	let lastLastLoading = whenLastLoading
	let thisRequestId = requestId
	let sortProperty = analysisSelections.sorting.recency == 100 ? 'year' : analysisSelections.sorting.participants == 100 ? 'N' : 'score'
	if (start == 0) {
		whenDigitizedLoaded = new Promise(resolve => digitizedResolved = resolve)
		studiesByIds = new Map()
		// reset all the queues
		requestId++
		thisRequestId = requestId
		studyStats.put({ groupedCount: 0, digitizedCount: 0 })
		// we first get the undigitized loading (in parallel)
		hasLoadedUndigitizedCount = false
		undigitizedOffset = 0
		loadingUndigitizedLevel = 1
		undigitizedQueue = []	
		whenUndigitizedLoaded = loadMoreUndigitized()
		let studies = await studyCollection
		if (thisRequestId != requestId)
			return []
		digitizedOffset = 0
		loadingDigitized = true
		digitizedQueue = [] // the queue of loaded studies, that haven't been merged into the final results yet
		digitizedStudiesLoaded.put([])
		// special case to get digitized studies to show up quickly before the article search query finishes
		// and then process digitized studies before waiting for the undigitized
		let returnCount = studies.length
		prepareDigitizedStudies(studies)
		studies = []
		// now use studies with good scores
		while (digitizedQueue.length > 0 && (sortProperty == 'year' ? digitizedQueue[0].year >= 2020 : digitizedQueue[0].score > 0.25)) {
			studies.push(digitizedQueue.shift())
		}
		whenDigitizedLoaded = null
		if (returnCount == FETCH_SIZE)
			whenDigitizedLoaded = searchForStudies(digitizedOffset, null, 5000).then(({ studies }) => {
				loadingDigitized = false
				digitizedResolved()
				prepareDigitizedStudies(studies)
			})
		else {
			loadingDigitized = false
			digitizedResolved()
		}
		if (studies.length > 0) {
			addToArticleList(studies)
			return studies
		}
		loadingDigitized = false
		await whenUndigitizedLoaded
		// otherwise wait for undigitized studies to load
	} else {
		await lastLastLoading
		await whenUndigitizedLoaded
	}
	if (whenDigitizedLoaded)
		await whenDigitizedLoaded
	if (thisRequestId != requestId)
		return []
	
	if (undigitizedQueue.length == 0 && loadingUndigitizedLevel) {
		whenUndigitizedLoaded = loadMoreUndigitized()
	}
	if (whenUndigitizedLoaded)
		await whenUndigitizedLoaded
	if (thisRequestId != requestId)
		return []
	// now merge from queues
	let results = []
	let requestedCount = end - start
	while ((digitizedQueue.length > 0 || !loadingDigitized) && (undigitizedQueue.length > 0 || !loadingUndigitizedLevel) &&
		(digitizedQueue.length > 0 || undigitizedQueue.length > 0)) {
		let nextDigitized = digitizedQueue[0]
		let nextUndigitized = undigitizedQueue[0]
		if ((nextDigitized ? nextDigitized[sortProperty] != null ? nextDigitized[sortProperty] : -10000 : -Infinity) >=
				(nextUndigitized ? nextUndigitized[sortProperty] != null ? Math.max(nextUndigitized[sortProperty], -1) : -10000 : -Infinity)) {
			results.push(digitizedQueue.shift())
		} else {
			results.push(undigitizedQueue.shift())
		}
		if (results.length >= requestedCount) // once we have done what was requested of us
			break
	}
	return results
}
let isLoadingUndigitized, missingStudiesWarned

function addToArticleList(studies) {
	for (let study of studies) {        
		allArticles.push(study)
		if (study.additionalStudies)
			allArticles.push(...study.additionalStudies)
	}
	allArticlesVar.updated()
}
export async function getCurrentBluntQuery(allowCTG?) {
	let { interventions, outcomes, characteristics } = structuredQuery.valueOf()
	let querySettings = {
		interventions,
		outcomes,
		characteristics,
		forArticles: true,
		preferRCT: analysisType != 'cohort',
		allowCTG,
	}
	return {
		bluntQuery: await getBluntQuery(querySettings),
		querySettings,
	}
}
async function loadMoreUndigitized() {
	isLoadingUndigitized = true
	let limit = 100
	if (!structuredQuery.valueOf() || structuredQuery.valueOf().isAsking)
		return { articles: [] }
	let thisRequestId = requestId
	let { interventions, outcomes, characteristics } = structuredQuery.valueOf()
	let { querySettings, bluntQuery } = await getCurrentBluntQuery()
	let sortOrder = '&order=' + (sorting.recency > 80 ? 'published-desc' : 'scoretitle-desc')
	let { bluntQuery: externalBluntQuery } = await getCurrentBluntQuery(true)
	lastDocSearchQuery.put('https://search.doctorevidence.com/search?query=' + encodeURIComponent(externalBluntQuery) + '&type=articles&roles=b6ddf' + sortOrder)
	if (!bluntQuery) {
		loadingUndigitizedLevel = 0
		studyStats.undigitizedCount.put(0)
		return
	}
	let url = '/portal/articles?query=' + encodeURIComponent(bluntQuery) +
		'&limit=' + limit + '&offset=' + undigitizedOffset + '&selections=' + analysisSelections.id +
		'&characteristics=' + encodeAttributeAsValue(characteristics) +
		'&interventions=' + encodeAttributeAsValue(interventions) +
		'&outcomes=' + encodeAttributeAsValue(outcomes) +
		'&attributes=' // legacy, can remove after 2.3

	if (includeFlagged.valueOf()) {
		url += '&includeFlagged=true'
	}
	if (sorting.valueOf()) {
		url += '&recency=' + sorting.recency
		url += '&participants=' + sorting.participants
		url += '&analyzable=' + sorting.analyzable
	}
	let articleSearch = await get(url).catch(error => {
		console.error('Error retrieving DOC Search article list', error)
		loadingUndigitizedLevel = 0
		if (!missingStudiesWarned) {
			const questionBar = document.querySelector('.question-bar')
			questionBar.append(new MissingResultsToast)
			missingStudiesWarned = true
		}
		return { articles: [], count : 0 }
	})
	lastDocSearchQuery.put(articleSearch.baseUrl + 'search?query=' + encodeURIComponent(externalBluntQuery) + '&type=articles&roles=b6ddf' + sortOrder)


	if (thisRequestId != requestId)
		return
	isLoadingUndigitized = false
	flagFiltered.put(articleSearch.flagFiltered)
	if (!hasLoadedUndigitizedCount) {
		setTimeout(async () => {
			if (!isLoadingUndigitized && !hasLoadedUndigitizedCount) {
				hasLoadedUndigitizedCount = true
				let bluntQuery = await getBluntQuery(Object.assign(querySettings, {
					inTitle: false,
					onlyRCT: false,
					onlyPubmedPills: false,
					outcomes: []
				}))
				let { count } = await articleCountCache.get(bluntQuery, () => get('/portal/article-count?query=' + encodeURIComponent(bluntQuery)))
				if (thisRequestId != requestId)
					return
				studyStats.undigitizedCount.put(count)
			}
		}, 50)
	}
	undigitizedOffset += articleSearch.articles.length + articleSearch.removedCount
	if (articleSearch.articles.length < (limit - articleSearch.removedCount)) {
		loadingUndigitizedLevel-- // next level down (more general or all done loading these)
		undigitizedOffset = 0 // reset offset for next query
	}
	studyStats.set('groupedCount', studyStats.get('groupedCount') + articleSearch.removedCount)
	let articles = articleSearch.articles.filter(article => {
		if (article.DOI)
			article.DOI = article.DOI.toLowerCase()
		let matchingStudy = studiesByIds.get(article.id)
		if (matchingStudy) { // same exact study, don't do anything more and skip it
			studyStats.set('groupedCount', studyStats.get('groupedCount') + 1)
			return false
		}
		studiesByIds.set(article.id, article)
		let acronym = normalizeAcronym(article.acronym || article.acronyms?.[0])
		let trialIds = article.trialIds || []
		let metadata = article.metadata
		let ctgId = article.CTGId
		let medlineId = article.medlineId
		let groupHash = article.groupHash
		let euId = article.sourceReferenceId?.match(/EUCTR(.+)/)?.[1]
		let DOI = article.DOI
		if (!matchingStudy) {
			let allTrialIds = [...trialIds, ...(article.acronym ? [article.acronym] : article.acronyms || []).map(normalizeAcronym)]
			if (ctgId)
				allTrialIds.push(ctgId)
			if (euId)
				allTrialIds.push(euId)
			if (medlineId)
				allTrialIds.push(medlineId)
			if (DOI)
				allTrialIds.push(DOI)
			if (groupHash)
				allTrialIds.push(groupHash)
			for (let trialId of allTrialIds) {
				matchingStudy = studiesByIds.get(trialId)
				if (matchingStudy)
					break
			}
		}
		if (matchingStudy) {
			if (!matchingStudy.N) // if for some reason the digitized study doesn't have the N, but the DOC search version does
				matchingStudy.N = article.N
			if (![matchingStudy, ...(matchingStudy.additionalStudies || [])].find(matchingStudy => matchingStudy.articleId == article.metadata.ArticleId)) {
				if (!matchingStudy.additionalStudies)
					matchingStudy.additionalStudies= []
				article.parentStudy = matchingStudy
				matchingStudy.additionalStudies.push(article)
				articleAddedListener(matchingStudy)
			} // else already in there
		} else {
			// no digitized study, use the DOC search article
			article.Study = {
				InternalMetadata: {},
			}
			if (article.medlineId)
				studiesByIds.set(article.medlineId, article)
			if (article.DOI)
				studiesByIds.set(article.DOI, article)
		}
		let docDataId = trialIds.find(id => +id > 0)
		let targetStudy = matchingStudy || article
		let trialId = targetStudy.trialId
		if (!trialId)
			trialId = targetStudy.trialId = {}
		if (acronym && !trialId.acronym) {
			studiesByIds.set(acronym, targetStudy)
		}
		if (ctgId && !trialId.ctg) {
			studiesByIds.set(ctgId, targetStudy)
		}
		if (euId && !trialId.eu) {
			studiesByIds.set(euId, targetStudy)
		}
		if (acronym && !trialId.acronym) {
			studiesByIds.set(acronym, targetStudy)
		}
		if (docDataId && !trialId.docdata) {
			studiesByIds.set(docDataId, targetStudy)
		}
		if (medlineId && !trialId.medlineId) {
			studiesByIds.set(medlineId, targetStudy)
		}
		if (DOI && !trialId.DOI) {
			studiesByIds.set(DOI, targetStudy)
		}
		if (groupHash)
			studiesByIds.set(groupHash, targetStudy)
		if (matchingStudy)
			studyStats.set('groupedCount', studyStats.get('groupedCount') + 1)

		return !matchingStudy
	})
	addToArticleList(articles)
	undigitizedQueue.push(...articles)
}

let articleAddedListener
export function onArticleAdded(listener) {
	articleAddedListener = listener
}

function normalizeAcronym(acronym) {
	return acronym?.replace(/[\s_]/g, '-')
}

const prepareDigitizedStudies = (studies) => {
	let seletedInterventions = selectedTreatmentIds.valueOf()
	let currentSelectedOutcomes = selectedOutcomes.valueOf()
	let interventionCollections = []
	let interventionsAvailable = studiesSearch.valueOf()?.interventions
	for (let seletedInterventionId of seletedInterventions) {
		let analyzeAs = analyzeTreatmentAs.get(seletedInterventionId)
		if (analyzeAs) {
			if (!interventionCollections.includes(analyzeAs))
				interventionCollections.push(analyzeAs)
		} else {
			let selectedIntervention = interventionsAvailable?.find(intervention => intervention.id == seletedInterventionId)
			analyzeAs = selectedIntervention ? selectedIntervention.label : seletedInterventionId
		}
	}
	let timepoints = []
	let studySummaries = studies.map(study => {
		study.digitized = true
		if (study.DOI)
			study.DOI = study.DOI.toLowerCase()
		const addById = (study) => {
			for (let type of ['articleId', 'medlineId', 'DOI']) {
				if (study[type])
					studiesByIds.set(study[type], study)
			}
		}
		addById(study)
		if (study.additionalStudies) {
			for (let additionalStudy of study.additionalStudies || []) {
				additionalStudy.parentStudy = study
				addById(additionalStudy)
			}
		}
		let trialIds = [...study.trialIds, ...study.acronyms, ...(study.additionalStudies || []).flatMap(additionalStudy =>
			[...(additionalStudy.trialIds || []), ...(additionalStudy.acronyms || [])])].map(normalizeAcronym)
			
		let counts = new Map<string, number>()
		for (let trialId of trialIds) {
			counts.set(trialId, (counts.get(trialId) || 0) + 1)
		}
		trialIds = Array.from(counts).sort(([ aId, aCount], [ bId, bCount ]) => aCount > bCount ? -1 : aCount == bCount ? 0 : 1).map(([ id ]) => id)

		let idTypes = {}
		for (let trialId of trialIds) {
			let idType = +trialId > 0 ? 'docdata' : trialId.match(/NCT\d{8}/) ? 'ctg' : trialId.match(/[0-9\.]+\/\w+/) ? 'DOI' :
				trialId.match(/\d+-\d+-\d+/) ? 'eu' : 'acronym'
			if (idTypes[idType])
				continue // don't process the same type of id more than once
			else
				idTypes[idType] = trialId
			study.trialId = idTypes
			studiesByIds.set(trialId, study)
		}

		for (let outcome of study.outcomes) {
			if (outcome?.time?.days && currentSelectedOutcomes?.some(selectedOutcome => selectedOutcome.id == outcome.id)) {
				timepoints.push(outcome.time.days)
			}
		}
		return study
	})
	if (studySummaries.length > 0) {
		digitizedQueue.push(...studySummaries)
		digitizedStudiesLoaded.push(...studySummaries)
		digitizedOffset += studySummaries.length
		addToArticleList(studySummaries)
	}
}

export async function loadAllStudies() {
	if (loadingDigitized) {
		loadingDigitized = false
		let response = await searchForStudies(digitizedOffset, null, 10000)
		prepareDigitizedStudies(response.studies)
	}
}

function isAnalyzable(cell) {
	return cell.number != null && (cell.type == 'Binary' || cell.sd || cell.se)
}
export const selectedStudyData = new VArray()
let hasCount
selectedStudyData.subscribe(event => {
	let newLength = selectedStudyData.valueOf()?.length
	if ((newLength > 0 || (hasCount && newLength > -1)) && analysisSelections.get('selectedStudyCount') != newLength && (!(analysisSelections.get('locked') === undefined ? analysisSelections.get('favorite') : analysisSelections.get('locked'))
	|| isLiveEditing.valueOf())) {
		hasCount = true
		analysisSelections.set('selectedStudyCount', newLength)
	}
})
export function recordStudySelections() {
	let analysisDirectionality = isAnalysisOutcomePositive.property(selectedOutcomes.valueOf()[0]?.id)
	analysisDirectionality.put(Boolean(analysisDirectionality.valueOf())) // lock the directionality
	if (analysisType == 'bayesian') {
		let settings = analysisSelections.property('settings')
		if (!settings.get('seed'))
			settings.set('seed', Math.round(Math.random() * 0x80000000))
	}
	for (let rowData of selectedStudyData) {
		new StudyAnalysisModel(rowData.study).recordStudySelections()
	}
}
let currentScreening
export function setScreening(screening) {
	currentScreening = screening
}
export const getArticleIdOfVote = vote => vote.source == 'docdata' ? 'docdata/' + vote.uri : vote['article/guid']
function isStudyVisibleInTab(study) {
	let articleId = study.articleId || ('docdata/' + study.id)
	if (activeTab == 'screening') {
		return !currentScreening?.votes?.find(vote =>
							getArticleIdOfVote(vote) == articleId)
	} else if (activeTab == 'extract') {
		if (currentScreening)
			return currentScreening.votes.valueOf()?.find(vote =>
							getArticleIdOfVote(vote) == articleId && (vote.type.startsWith('Include') || vote.type.startsWith('Uncertain')))
		else
			return true
	} else if (activeTab == 'analyzable') {
		if (currentScreening)
			return currentScreening.votes.valueOf()?.find(vote =>
							getArticleIdOfVote(vote) == articleId && vote.type.startsWith('Include') && study.hasAnalyzableData)
		return study.hasAnalyzableData
	} else
		return true
}

export const focusNextStudy = () => {
	let studyId = focusedStudyId.valueOf()
	let next
	for (let otherStudy of allArticles) {
		if (next && !otherStudy.parentStudy && isStudyVisibleInTab(otherStudy)) {
			focusedStudy.put(otherStudy)
			return otherStudy
		}
		if (studyId == otherStudy.id)
			next = true
	}
}

export const focusPreviousStudy = () => {
	let studyId = focusedStudyId.valueOf()
	let previous
	for (let otherStudy of allArticles) {
		if (studyId == otherStudy.id){
			if (previous) {
				focusedStudy.put(previous)
				return previous
			}
			else
			    return null
		}
		if (!otherStudy.parentStudy && isStudyVisibleInTab(otherStudy))
			previous = otherStudy
	}
}
export const defaultStudyExpanded = reactive(true).to(() =>
	localStorage.getItem('default-expanded') == 'true' ||
		(app.user?.then ? app.user.then(user => user.Settings.AutoExpandRows) : app.user.Settings.AutoExpandRows),
	expanded => {
		let settings = app.user.Settings
		settings.AutoExpandRows = expanded
		localStorage.removeItem('default-expanded')
		return patch<User>('/portal/User/' + app.user.Id, {
			data: {
				Settings: settings,
			}
		})
	})
